/**
 * Runtime-loaded chart sets. Most providers are static, but the BSB/KAP charts
 * (licensed, local-only) are produced by the converter and described by a
 * manifest, so they load at runtime into here and the BSB provider reads them
 * via resolveProvider. Empty until the manifest loads (or when there are none).
 *
 * This store also holds two bits of chart-navigation state: the current map
 * view bounds (so the chart-source UI knows whether a chart is actually on
 * screen) and a "focus" command the UI fires to fly the map to a chart whose
 * coverage is somewhere else (e.g. real Antares charts up in Scotland while the
 * sim boat sits in the fictional training area).
 */
import { create } from 'zustand';
import type { RasterChart } from '@/chart-sources';
import type { ChartSource } from '@/types';

/** A lon/lat box: [minLon, minLat, maxLon, maxLat]. */
export type LonLatBounds = [number, number, number, number];

interface ChartSourcesState {
  bsbCharts: RasterChart[];
  bsbLoaded: boolean;
  setBsbCharts: (charts: RasterChart[]) => void;

  noaaCharts: RasterChart[];
  noaaLoaded: boolean;
  setNoaaCharts: (charts: RasterChart[]) => void;

  /** Active download-on-demand pack charts (online + downloaded), merged into the
   *  Antares provider by resolveProvider. Published by the chart-packs store. */
  packCharts: RasterChart[];
  setPackCharts: (charts: RasterChart[]) => void;

  /**
   * Cell sources from any VECTOR pack that is currently on. MapView folds these
   * in alongside the bundled sources when it works out which cells to load, so a
   * pack's cells behave exactly like bundled ones once the pack is switched on.
   */
  packCellSources: ChartSource[];
  setPackCellSources: (sources: ChartSource[]) => void;

  /** Current visible map extent, kept fresh by MapView for the chart-source UI. */
  viewBounds: LonLatBounds | null;
  setViewBounds: (b: LonLatBounds | null) => void;

  /**
   * A request to fly the map to a chart's bounds. A nonce makes repeated taps on
   * the same chart fire again, the same trick recentreRequest uses. MapView
   * watches it, fits the bounds, and the chart appears.
   */
  chartFocus: { bounds: LonLatBounds; nonce: number } | null;
  focusChart: (bounds: LonLatBounds) => void;
}

export const useChartSourcesStore = create<ChartSourcesState>((set) => ({
  bsbCharts: [],
  bsbLoaded: false,
  setBsbCharts: (bsbCharts) => set({ bsbCharts, bsbLoaded: true }),

  noaaCharts: [],
  noaaLoaded: false,
  setNoaaCharts: (noaaCharts) => set({ noaaCharts, noaaLoaded: true }),

  packCharts: [],
  setPackCharts: (packCharts) => set({ packCharts }),

  packCellSources: [],
  setPackCellSources: (packCellSources) => set({ packCellSources }),

  viewBounds: null,
  setViewBounds: (viewBounds) => set({ viewBounds }),

  chartFocus: null,
  focusChart: (bounds) =>
    set((s) => ({ chartFocus: { bounds, nonce: (s.chartFocus?.nonce ?? 0) + 1 } })),
}));
